import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';

import type { DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandL, BrandS } from '@sorare/core/src/atoms/typography';
import { Card } from '@sorare/core/src/components/card/Card';
import { droppedCard } from '@sorare/core/src/components/card/style';
import { sportProfile } from '@sorare/core/src/contexts/currentUser/queries';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { waitMs } from '@sorare/core/src/lib/animation/waitMs';
import { glossary } from '@sorare/core/src/lib/glossary';
import { SetColorTheme } from '@sorare/core/src/set/SetColorTheme/index.tsx';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionAnimation } from '#src/components/commonCard/RevealPackCardsDialog/CollectionAnimation/index.tsx';
import {
  BaseLayout,
  BottomArea,
} from '#src/components/commonCard/RevealPackCardsDialog/ui.ts';

import { AnimatedContentSwap } from './AnimatedContentSwap/index.tsx';
import type {
  PackRecap_anyCardInterface,
  claimCardsFromPackMutation,
  claimCardsFromPackMutationVariables,
} from './__generated__/index.graphql.ts';

const cardDisappearAnimation = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-120px);
  }
`;
const cardEnterAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(120px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const disappearingStyle = css`
  .disappearing & {
    opacity: 1;
    transform: translateY(0);
    animation: ${cardDisappearAnimation} 0.5s calc(var(--index, 0) * 0.05s)
      var(--ease-in-cubic) forwards;
  }
`;

const Top = styled.div`
  grid-area: top;
  padding: var(--double-unit) var(--double-unit) 0;
`;
const Center = styled(Vertical).attrs({ center: true, gap: 4 })`
  grid-area: center;
  min-height: 0;
  align-self: stretch;
  justify-self: stretch;
  padding: 0 var(--double-unit);
`;
const Bottom = styled(BottomArea)`
  position: sticky;
  bottom: 0;
  justify-self: stretch;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--double-unit);
  padding: var(--double-unit);
  background: linear-gradient(
    to top,
    rgba(var(--c-rgb-black), 0.5) 40%,
    transparent
  );
`;
const titleStyle = css`
  opacity: 0;
  animation: ${fadeIn} 1s ease-in-out forwards;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--double-unit);
`;
const Title = styled(BrandL)`
  ${titleStyle}
  ${disappearingStyle}
`;
const Subtitle = styled(BrandS)`
  ${titleStyle}
  ${disappearingStyle}
`;
const Cards = styled(Horizontal).attrs({ center: true })`
  flex-wrap: wrap;
  max-width: var(--100vw);
  --gap: var(--double-unit);
  gap: var(--gap);
`;
const CardImgWrapper = styled.div`
  width: calc(calc(33 * var(--vw)) - var(--gap));
  @media ${tabletAndAbove} {
    width: 120px;
  }
  & > img {
    max-width: 100%;
    max-height: 25vh;
    aspect-ratio: var(--card-aspect-ratio);
    object-fit: contain;
    position: relative;
  }
  &.dropped {
    ${droppedCard}
  }
  opacity: 0;
  transform: translateY(120px);
  animation: ${cardEnterAnimation} 0.8s calc(var(--index) * 0.05s)
    var(--ease-out-slide) forwards;
  ${disappearingStyle}
`;
const CLAIM_CARDS_FROM_PACK_MUTATION = gql`
  mutation claimCardsFromPackMutation(
    $input: claimCardsFromPackInput!
    $sport: Sport!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        ...CurrentUseProvider_sportProfile
      }
      errors {
        message
        code
      }
      pack {
        id
        cards {
          slug
          ...CollectionAnimation_anyCardInterface
        }
      }
    }
  }
  ${CollectionAnimation.fragments.anyCardInterface}
  ${sportProfile}
` as TypedDocumentNode<
  claimCardsFromPackMutation,
  claimCardsFromPackMutationVariables
>;

type Props = {
  cards: PackRecap_anyCardInterface[];
  onContinue: () => void;
  droppedCards: DropCardInput[];
  onBack: () => void;
  isFirstPack?: boolean;
  packId: string;
  cardSetSlug: string;
  onCollectionAnimationActive: () => void;
};
export const PackRecap = ({
  cards,
  droppedCards,
  onContinue,
  onBack,
  isFirstPack,
  packId,
  cardSetSlug,
  onCollectionAnimationActive,
}: Props) => {
  const { sport } = useSportContext();
  const {
    flags: { disableCollectionAnimation = false },
  } = useFeatureFlags();

  const [claimCardsFromPack, { loading: claimCardsFromPackLoading }] =
    useMutation(CLAIM_CARDS_FROM_PACK_MUTATION, {
      showErrorsWithSnackNotification: true,
    });
  const [claimCardsFromPackMutionData, setClaimCardsFromPackMutionData] =
    useState<claimCardsFromPackMutation['claimCardsFromPack']>();

  const [disappearing, setDisappearing] = useState(false);
  const keptCards: typeof cards = [];
  const scrappedCards: typeof cards = [];
  cards.forEach(c => {
    if (droppedCards.some(card => card.cardSlug === c.slug)) {
      scrappedCards.push(c);
    } else {
      keptCards.push(c);
    }
  });
  const [collectionAnimationActive, setCollectionAnimationActive] =
    useState(false);

  const newlyKeptCards = keptCards.filter(card => !card.previouslyOwnedCard);

  return (
    <AnimatedContentSwap
      swap={collectionAnimationActive}
      back={
        <CollectionAnimation
          newCards={
            claimCardsFromPackMutionData?.pack?.cards.filter(card =>
              newlyKeptCards.some(c => c.slug === card.slug)
            ) || []
          }
          shouldAnimate={collectionAnimationActive}
          onAnimationEnd={onContinue}
          cardSetSlug={cardSetSlug}
        />
      }
    >
      <BaseLayout
        className={classNames('text-center', {
          disappearing,
        })}
      >
        <SetColorTheme setSlug={cardSetSlug}>
          <Top>
            <Title color="var(--c-white)">
              <Vertical center gap={2}>
                <StarBall color="var(--c-white)" />
                <FormattedMessage
                  defaultMessage="{count, plural, one {New signing} other {new signings}}"
                  values={{
                    count: cards.length,
                  }}
                />
              </Vertical>
            </Title>
          </Top>
        </SetColorTheme>
        <Center>
          {keptCards.length ? (
            <Cards>
              {keptCards.map((card, index) => (
                <CardImgWrapper
                  key={card.slug}
                  style={{ '--index': index } as React.CSSProperties}
                >
                  <Card card={card} width={320} />
                </CardImgWrapper>
              ))}
            </Cards>
          ) : (
            <BodyL color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="All players in the pack were scrapped" />
            </BodyL>
          )}
          {!!scrappedCards.length && (
            <Vertical center gap={1.5}>
              <Subtitle>
                <FormattedMessage defaultMessage="Scrapped Cards" />
              </Subtitle>
              <Cards>
                {scrappedCards.map((card, index) => (
                  <CardImgWrapper
                    key={card.slug}
                    className={classNames('dropped', {
                      disappearing,
                    })}
                    style={{ '--index': index } as React.CSSProperties}
                  >
                    <Card card={card} width={320} />
                  </CardImgWrapper>
                ))}
              </Cards>
            </Vertical>
          )}
        </Center>
        <Bottom>
          {!isFirstPack && (
            <Button color="secondary" onClick={onBack} size="medium" fullWidth>
              <FormattedMessage {...glossary.back} />
            </Button>
          )}
          <Button
            color="primary"
            onClick={async () => {
              const { data } = await claimCardsFromPack({
                variables: {
                  sport,
                  input: {
                    chosenCardSlugs: keptCards.map(c => c.slug),
                    packId,
                  },
                },
              });
              setClaimCardsFromPackMutionData(data?.claimCardsFromPack);

              setDisappearing(true);
              await waitMs(600 + keptCards.length * 100);
              if (newlyKeptCards.length > 0 && !disableCollectionAnimation) {
                setCollectionAnimationActive(true);
                onCollectionAnimationActive();
              } else {
                onContinue();
              }
            }}
            size="medium"
            loading={claimCardsFromPackLoading}
            fullWidth
          >
            <FormattedMessage {...glossary.continue} />
          </Button>
        </Bottom>
      </BaseLayout>
    </AnimatedContentSwap>
  );
};

PackRecap.fragments = {
  anyCardInterface: gql`
    fragment PackRecap_anyCardInterface on AnyCardInterface {
      slug
      previouslyOwnedCard
      ...Card_anyCard
    }
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<PackRecap_anyCardInterface>,
};
